import React, { useRef, useState } from "react";
import {
  IonContent,
  IonPage,
  IonFab,
  IonFabButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonCard,
  IonCardContent,
  IonSkeletonText,
  IonBadge,
  IonButton,
  RefresherEventDetail,
  IonToast,
  IonListHeader,
} from "@ionic/react";
import {
  addOutline,
  timeOutline,
  calendarOutline,
  createOutline,
  trashOutline,
  cameraOutline,
  alertCircleOutline,
  checkmarkCircleOutline,
} from "ionicons/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTimesheets, deleteTimesheet, Timesheet } from "@/api";
import { formatNumber } from "@/utils";
import EditTimesheetModal from "@components/Modals/EditTimesheetModal.tsx";
import { Dialog } from "@capacitor/dialog";
import ClientAddressLines from "@components/ClientAddressLines.tsx";
import { AppHeader } from "@/components/AppHeader";
import { Did } from "@/types";

type ViewMode = "week" | "month" | "all";

const Timesheets: React.FC = () => {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [showModal, setShowModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(
    null
  );
  const [showToast, setShowToast] = useState({
    show: false,
    message: "",
    color: "success" as "success" | "danger",
  });

  const getDateRange = () => {
    const now = new Date();
    const from = new Date();
    const to = new Date();

    switch (viewMode) {
      case "week":
        from.setDate(now.getDate() - now.getDay() + 1); // Monday
        to.setDate(from.getDate() + 6); // Sunday
        break;
      case "month":
        from.setDate(1);
        to.setMonth(from.getMonth() + 1);
        to.setDate(0);
        break;
      default:
        return {};
    }

    return {
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    };
  };

  const {
    data: timesheets,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["timesheets", viewMode],
    queryFn: () => getTimesheets(getDateRange()),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTimesheet,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["timesheets"],
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: ["dashboard"],
        exact: false,
      });
      setShowToast({
        show: true,
        message: "Uren verwijderd",
        color: "success",
      });
    },
    onError: () => {
      setShowToast({
        show: true,
        message: "Fout bij verwijderen uren",
        color: "danger",
      });
    },
  });

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    closeAllSliding();
    await refetch();
    event.detail.complete();
  };

  const handleEdit = (timesheet: Timesheet) => {
    closeAllSliding();
    setEditingTimesheet(timesheet);
    setShowModal(true);
  };

  const handleDelete = async (id: Did) => {
    const { value: confirmed } = await Dialog.confirm({
      title: "Uren verwijderen",
      message: "Weet je zeker dat je deze uren wilt verwijderen?",
    });

    closeAllSliding();
    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  const handleModalClose = async (props?: {
    invalidate: boolean;
    toast: {
      message: string;
      color: "success" | "danger";
    };
  }) => {
    setShowModal(false);
    setEditingTimesheet(null);
    if (props?.toast) {
      setShowToast({
        show: true,
        message: props.toast.message,
        color: props.toast.color,
      });
    }
    if (props?.invalidate) {
      await queryClient.invalidateQueries({
        queryKey: ["timesheets"],
        exact: false,
      });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    }
  };

  const groupTimesheetsByDate = (timesheets: Timesheet[]) => {
    const grouped: { [key: string]: Timesheet[] } = {};
    timesheets?.forEach((timesheet) => {
      if (!grouped[timesheet.date]) {
        grouped[timesheet.date] = [];
      }
      grouped[timesheet.date].push(timesheet);
    });
    return grouped;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("nl-NL", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  const calculateDayTotal = (timesheets: Timesheet[]) => {
    return timesheets.reduce((sum, t) => sum + t.hours, 0);
  };

  const calculateTotalHours = () => {
    return timesheets?.reduce((sum, t) => sum + t.hours, 0) || 0;
  };

  const groupedTimesheets = groupTimesheetsByDate(timesheets || []);

  const listRef = useRef<HTMLIonListElement>(null);
  const closeAllSliding = () => {
    listRef.current?.closeSlidingItems();
  };

  return (
    <IonPage className="timesheets-page">
      <AppHeader title="Uren" />

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="ion-padding">
          {/* View Mode Selector */}
          <IonSegment
            color="primary"
            value={viewMode}
            onIonChange={(e) => setViewMode(e.detail.value as ViewMode)}
          >
            <IonSegmentButton value="week">
              <IonLabel>Deze week</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="month">
              <IonLabel>Deze maand</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="all">
              <IonLabel>Alles</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Total Hours Card */}
          <IonCard color="primary">
            <IonCardContent>
              <div className="ion-display-flex ion-align-items-center ion-flex-row">
                <IonIcon
                  color="light"
                  icon={timeOutline}
                  className="font-size-36 ion-padding-end"
                />
                <div>
                  <div className="font-size-24 font-weight-600">
                    {formatNumber(calculateTotalHours())} uur
                  </div>
                  <div className="font-size-12 ion-text-uppercase">
                    {viewMode === "week" && "Deze week"}
                    {viewMode === "month" && "Deze maand"}
                    {viewMode === "all" && "Totaal"}
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Timesheets List */}
          {isLoading ? (
            <IonCard>
              <IonCardContent>
                <IonSkeletonText
                  animated
                  style={{ height: "60px", marginBottom: "10px" }}
                />
                <IonSkeletonText
                  animated
                  style={{ height: "60px", marginBottom: "10px" }}
                />
                <IonSkeletonText animated style={{ height: "60px" }} />
              </IonCardContent>
            </IonCard>
          ) : Object.keys(groupedTimesheets).length === 0 ? (
            <IonCard>
              <IonCardContent className="ion-text-center">
                <IonIcon
                  color="primary"
                  icon={calendarOutline}
                  className="font-size-64"
                />
                <p className="ion-padding">Nog geen uren geregistreerd</p>
                <IonButton
                  className="ion-margin-top"
                  expand="block"
                  onClick={() => setShowModal(true)}
                >
                  Voeg uren toe
                </IonButton>
              </IonCardContent>
            </IonCard>
          ) : (
            Object.entries(groupedTimesheets)
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([date, dayTimesheets]) => (
                <IonCard key={date}>
                  <IonCardContent>
                    <IonListHeader lines="full">
                      <IonLabel>{formatDate(date)}</IonLabel>
                      <IonBadge color="primary" className="ion-margin-bottom">
                        {formatNumber(calculateDayTotal(dayTimesheets))}u
                      </IonBadge>
                    </IonListHeader>
                    <IonList
                      lines="full"
                      ref={listRef}
                      className="timesheets-list"
                    >
                      {dayTimesheets
                        .filter((t) => t.id)
                        .map((timesheet) => {
                          const isUnknownProject = !timesheet.project_id;

                          return (
                            <IonItem key={timesheet.id}>
                              <IonLabel>
                                {isUnknownProject ? (
                                  <>
                                    <p
                                      className="font-size-12 font-weight-600 ion-margin-bottom"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <IonIcon icon={alertCircleOutline} />
                                      Onbekend project
                                    </p>
                                    {timesheet.unknown_work_description && (
                                      <p className="font-size-12 ion-margin-bottom">
                                        {timesheet.unknown_work_description}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {timesheet.project?.client && (
                                      <ClientAddressLines
                                        client={timesheet.project.client}
                                      />
                                    )}
                                    <p
                                      className="font-size-12 font-weight-600 ion-margin-bottom"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      {timesheet.project?.status ===
                                        "completed" && (
                                        <IonIcon
                                          icon={checkmarkCircleOutline}
                                          color="success"
                                          title="Project afgerond"
                                        />
                                      )}
                                      {timesheet.project?.name}
                                    </p>
                                  </>
                                )}
                                <p className="font-size-12">
                                  {timesheet.note ?? ""}
                                </p>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  {timesheet.photos &&
                                    timesheet.photos.length > 0 && (
                                      <IonIcon
                                        icon={cameraOutline}
                                        color="medium"
                                        className="tw-text-sm"
                                      />
                                    )}
                                  <IonBadge
                                    color="primary"
                                    className="px-2 py-1"
                                  >
                                    {formatNumber(timesheet.hours)}u
                                  </IonBadge>
                                </div>
                              </IonLabel>
                              <div
                                slot="end"
                                className="tw-flex tw-items-center tw-gap-2"
                              >
                                <IonButton
                                  onClick={() => handleEdit(timesheet)}
                                >
                                  <IonIcon
                                    slot="icon-only"
                                    color="light"
                                    icon={createOutline}
                                  />
                                </IonButton>
                                <IonButton
                                  color="danger"
                                  onClick={() =>
                                    handleDelete(timesheet.id as Did)
                                  }
                                >
                                  <IonIcon
                                    slot="icon-only"
                                    color="light"
                                    icon={trashOutline}
                                  />
                                </IonButton>
                              </div>
                            </IonItem>
                          );
                        })}
                    </IonList>
                  </IonCardContent>
                </IonCard>
              ))
          )}
        </div>

        {/* Add Timesheet Modal */}
        <EditTimesheetModal
          timesheet={editingTimesheet ?? undefined}
          showModal={showModal}
          handleClose={handleModalClose}
        />

        {/* FAB */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => setShowModal(true)}>
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>

        {/* Toast */}
        <IonToast
          isOpen={showToast.show}
          onDidDismiss={() => setShowToast({ ...showToast, show: false })}
          message={showToast.message}
          duration={2000}
          color={showToast.color}
        />
      </IonContent>
    </IonPage>
  );
};

export default Timesheets;
